import React from 'react';
import { ArrowRightIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from './ui/Button';
import { Field } from './ui/Field';
import { useLeadForm } from '../hooks/useLeadForm';
import { useConsult } from '../contexts/ConsultContext';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/fc1be7a7-2f5d-462e-95e4-3d8f942ca56a.jpg';
const proofPoints = ['No surgery, no needles', 'Zero downtime', 'FDA-cleared'];
interface HeroProps {
  layout?: 'split' | 'centered';
}
export function Hero({
  layout = 'split'
}: HeroProps) {
  const reduce = useReducedMotion();
  const {
    openConsult
  } = useConsult();
  const {
    fields,
    errors,
    status,
    setField,
    submit
  } = useLeadForm({
    require: ['zip']
  });
  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void submit(() => openConsult('hero-zip'));
  };
  const eyebrow = <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-royal-700" data-id="element-61" data-mp="380w020y5mzeq">
      <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-62" data-mp="3j9ahhe4fas5o" />
      The world&apos;s #1 energy-based aesthetic device
    </p>;
  const heading = <h1 className="font-display text-[2.75rem] leading-[1.05] tracking-tight text-ash-900 sm:text-6xl lg:text-[4.25rem]" data-id="element-63" data-mp="39aur5dd7zmx8">
      Lift the jawline you had.
      <span className="block text-royal-600" data-id="element-64" data-mp="3ebisosz2rm5p">Skip the scalpel.</span>
    </h1>;
  const sub = <p className="max-w-xl text-lg leading-relaxed text-ash-600" data-id="element-65" data-mp="30fy8lu7m0xwp">
      Focused ultrasound reaches the deep foundational layer surgeons lift — stimulating your own collagen in a
      single 60–90 minute visit, with no downtime afterward.
    </p>;
  const form = <form onSubmit={onSubmit} noValidate className="w-full" data-id="element-66" data-mp="2vjpywwc6lwwx">
      <div className="rounded-2xl border border-ash-200 bg-white p-2 shadow-[0_18px_48px_-24px_rgba(19,24,32,0.35)] sm:flex sm:items-end sm:gap-2" data-id="element-67" data-mp="2hjf81tavgibr">
        <Field label="Your zip code" value={fields.zip} onChange={v => setField('zip', v.replace(/\D/g, '').slice(0, 5))} placeholder="e.g. 85251" inputMode="numeric" autoComplete="postal-code" error={errors.zip} className="flex-1 px-2 pt-2 sm:pb-2 sm:pt-2" data-id="element-68" data-mp="2lk8vymobdx4n" />
        <Button type="submit" size="lg" disabled={status === 'loading'} className="mt-3 w-full sm:mb-2 sm:mt-0 sm:w-auto" data-id="element-69" data-mp="ddf4wfnolwbe">
          {status === 'loading' ? 'Finding providers…' : 'Find a provider near me'}
          {status !== 'loading' && <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-70" data-mp="15zzr71pavi83" />}
        </Button>
      </div>
      <p className="mt-3 text-xs text-ash-500" data-id="element-71" data-mp="114kkctppmml6">
        Free consultation · No obligation · We never sell your information.
      </p>
    </form>;
  const points = <ul className="flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-72" data-mp="3luiaiu6w15my">
      {proofPoints.map(point => <li key={point} className="flex items-center gap-2 text-sm font-medium text-ash-700" data-id="element-73" data-mp="3hpsglavsi14o">
          <CheckIcon className="h-4 w-4 text-turq-500" aria-hidden="true" data-id="element-74" data-mp="1xaw06rmvemay" />
          {point}
        </li>)}
    </ul>;
  return <section id="top" className="relative w-full overflow-hidden bg-ash-50" data-id="element-75" data-mp="269zi3saawvnv">
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-1/2 bg-white lg:block" aria-hidden="true" data-id="element-76" data-mp="267whkebivk4w" />
      <div className={`relative mx-auto w-full max-w-7xl px-5 py-14 lg:px-8 lg:py-24 ${layout === 'centered' ? '' : 'lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16'}`} data-id="element-77" data-mp="2g0c1hh6dqisf">
        <motion.div initial={reduce ? false : {
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className={`flex flex-col gap-6 ${layout === 'centered' ? 'mx-auto max-w-2xl text-center items-center' : ''}`} data-id="element-78" data-mp="2p1jvhn1v4xgp">
          {eyebrow}
          {heading}
          {sub}
          <div className={layout === 'centered' ? 'w-full max-w-xl' : 'max-w-xl'} data-id="element-79" data-mp="4aup0ry9f1ob">{form}</div>
          {points}
        </motion.div>

        {layout === 'split' ? <motion.div initial={reduce ? false : {
        opacity: 0,
        scale: 0.97
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.06,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative mt-12 lg:mt-0" data-id="element-80" data-mp="3vk1i5vsfrww6">
            <img src={HERO_IMAGE} alt="Woman in her late forties with visibly lifted, natural-looking skin" className="aspect-[4/5] w-full rounded-3xl object-cover" data-id="element-81" data-mp="3hqjz0c3nwm6o" />
            <div className="absolute -bottom-6 left-6 right-6 rounded-2xl border border-ash-200 bg-white p-5 shadow-[0_24px_60px_-30px_rgba(19,24,32,0.5)] sm:left-auto sm:right-8 sm:w-64" data-id="element-82" data-mp="3rhwv5fwhtyii">
              <p className="font-display text-3xl text-royal-600" data-id="element-83" data-mp="2yxogad30b4km">4M+</p>
              <p className="mt-1 text-sm leading-snug text-ash-600" data-id="element-84" data-mp="2u06iniufwbrx">
                treatments performed worldwide by certified providers.
              </p>
            </div>
          </motion.div> : <div className="mt-14 overflow-hidden rounded-3xl" data-id="element-85" data-mp="1eaorren881lf">
            <img src={HERO_IMAGE} alt="Woman in her late forties with visibly lifted, natural-looking skin" className="h-[340px] w-full object-cover object-top lg:h-[440px]" data-id="element-86" data-mp="zl95obyn4kc2" />
          </div>}
      </div>
    </section>;
}